"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import { Box, Card, CardContent, Divider, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import type { YearlyRow } from "../types";
import { formatBillions } from "../utils";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type Props = {
  title?: string;
  rows: YearlyRow[];
  years: number[];
  columns: string[];
  palette: string[];
  colorMap?: Record<string, string>;
  labelFormatter?: (label: string) => string;
  textColor?: string;
};

export default function CommitmentsByDenominationTreemap({
  title = "Commitments by Denomination",
  rows,
  years,
  columns,
  palette,
  colorMap,
  labelFormatter,
  textColor = "white",
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    const formatLabel = (label: string) => labelFormatter?.(label) ?? label;

    const yearSet = new Set(years);

    const totals: Record<string, number> = {};
    rows.forEach((row) => {
      if (!yearSet.has(Math.round(Number(row.DocumentYear)))) return;
      columns.forEach((col) => {
        totals[col] = (totals[col] ?? 0) + (Number(row[col]) || 0);
      });
    });

    const data = columns
      .map((col, i) => ({
        name: col,
        value: totals[col] ?? 0,
        itemStyle: { color: colorMap?.[col] ?? palette[i % palette.length] },
      }))
      .filter((d) => d.value > 0);

    return {
      tooltip: {
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number };
          return `${formatLabel(p.name ?? "")}<br/><b>$${formatBillions(p.value ?? 0)}</b>`;
        },
      },
      series: [
        {
          type: "treemap",
          data,
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          visibleMin: 200,
          label: {
            show: true,
            fontSize: 12,
            fontWeight: "600",
            color: textColor,
            lineHeight: 15,
            overflow: "truncate",
            formatter: (params: unknown) => {
              const p = params as { name?: string; value?: number };
              return `${formatLabel(p.name ?? "")} $${formatBillions(p.value ?? 0)}`;
            },
          },
          itemStyle: {
            gapWidth: 3,
            borderWidth: 0,
          },
          emphasis: { disabled: true },
        },
      ],
    } as EChartsOption;
  }, [colorMap, columns, labelFormatter, palette, rows, years, textColor]);

  return (
    <Card sx={{ border: "#e6e6e6 solid 1px" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 0,
            mb: 1,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
            {title}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <ReactECharts
          option={option}
          style={{ height: 360, width: "100%" }}
          notMerge
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 1,
          }}
        >
          <Box
            component="img"
            src="/SDF-Logo.png"
            alt="Sovereign Debt Forum logo"
            sx={{
              width: { xs: 92, sm: 110 },
              height: "auto",
              flexShrink: 0,
              opacity: 0.7,
            }}
          />
          <Typography
            sx={{
              mt: 1,
              fontSize: "0.7rem",
              color: "#626367",
              textAlign: "left",
              lineHeight: "1rem",
            }}
          >
            This data reflects the documents available on our platform, not all
            sovereign debt.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
